import React, { useContext, useEffect, useState } from 'react';
import i18n from 'i18next';
import {
    AppleLargeSvg,
    ArrowBackSvg,
    FacebookSvg,
    GoogleLargeSvg,
    LanguageSvg,
    MailLargeSvg,
    TelegramLargeSvg,
    VkLargeSvg,
    YandexLargeSvg,
} from '../../icons';
import Modal from '../modal/Modal';
import { AUTH_TYPE, IHeaderProps, ILanguages } from '../../interfaces';
import { LANGUAGES, LANGUAGES_PRICES } from '../../i18n';
import Closure from '../closure';
import { MainContext } from '../../context/main.context';
import { PROFILE_LOGO } from '../../common/constants';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SupportTickets from '../support/tickets';


export default function Header(props: IHeaderProps) {
    const { origin, userData, paymentsData } = props;
    const { language, changeLanguage } = i18n;
    const [ openLanguageMenu, setOpenLanguageMenu ] = useState(false);
    const { source, modalContent, settings, setModalContent } = useContext(MainContext);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect((): void => {
        if (origin === 'prices' && !['ru', 'en'].includes(language)) {
            _changeLanguage('en');
        }
    }, []);

    const _changeLanguage = (newLang: string): void => {
        if (newLang === language) {
            setOpenLanguageMenu(false);
            return;
        }

        changeLanguage(newLang).then(r => null);
        setOpenLanguageMenu(false);
    }

    const _renderLanguages = () => {
        let _LANGUAGES: ILanguages[] = origin !== 'prices' ? [ ...LANGUAGES ] : [ ...LANGUAGES_PRICES ];

        return (
            <div className="languages-modal">
                {
                    _LANGUAGES.map((lang: ILanguages, index: number) => {
                        const { id, title } = lang;
                        const key: string = `${id}_${index}`;

                        return (
                            <div
                                key={key}
                                className="languages-modal-item"
                                onClick={() => _changeLanguage(id)}
                                style={{background: language === id ? 'rgb(227 227 227)' : 'unset'}}
                            >
                                {title}
                            </div>
                        );
                    })
                }
            </div>
        );
    }

    const _closeLanguageModal = (e: MouseEvent): void => {
        e.stopPropagation();

        setOpenLanguageMenu(false);
    }

    const _renderLanguageMenu = () => {
        return (
            <div className="language" onClick={() => setOpenLanguageMenu(true)}>
                <div className="language-value">
                    {language.toUpperCase()}
                </div>
                <LanguageSvg/>
                <Modal
                    hideCloseButton
                    open={openLanguageMenu}
                    onClose={(e: MouseEvent) => _closeLanguageModal(e)}
                >
                    {_renderLanguages()}
                </Modal>
            </div>
        )
    }

    const _renderAuthHeader = () => {
        return (
            <div className={`header ${!modalContent ? 'header-platform-auth' : 'header-modal-content'}`}>
                {!modalContent && _renderLanguageMenu()}
                {<Closure origin={origin} source={source}/>}
            </div>
        );
    }

    const _renderPaymentsHeader = () => {
        return (
            <div className="header header-platform-auth">
                <div className="header-payments-project-box">
                    <img className="header-payments-project-logo" src={PROFILE_LOGO} alt=""/>
                    <div className="text-overflow">{paymentsData?.nickName || 'USER'}</div>
                </div>
                <div className="header-payments-right-box">
                    {!modalContent && _renderLanguageMenu()}
                    {<Closure origin={origin} source={source}/>}
                </div>
            </div>
        );
    }

    const _renderTransactionsHeader = () => {
        return (
            <div className="header header-platform-auth">
                <div className="header-profile-box">
                    {_renderProfileAvatarAuth()}
                    <div className="text-overflow">
                        {userData?.nickName || 'USER'}
                    </div>
                </div>
                <Closure origin={origin} source={source}/>
            </div>
        );
    }

    const _renderProfileAvatarAuth = () => {
        const type: AUTH_TYPE | undefined = userData?.currentAuthType;

        if (type === 'google') {
            return <GoogleLargeSvg/>;
        } else if (type === 'apple') {
            return <AppleLargeSvg/>;
        } else if (type === 'vk') {
            return <VkLargeSvg/>;
        } else if (type === 'yandex') {
            return <YandexLargeSvg/>;
        } else if (type === 'telegram') {
            return <TelegramLargeSvg/>;
        } else if (type === 'facebook') {
            return <FacebookSvg width="30" height="30"/>;
        } else {
            return <MailLargeSvg/>;
        }
    }

    const _renderSupportTicketListHeader = () => {
        return (
            <div className="header header-arrow-space-between">
                <div className="header-support-center">
                    { t('support.ticketTitle') }
                </div>
                <Closure origin={origin} source={source}/>
            </div>
        );
    }

    const _renderSupportFirstTicketCreateHeader = () => {
        return (
            <div className="header header-arrow-space-between">
                <div className="header-support-center">
                    {t('support.createTicket')}
                </div>
                <Closure origin={origin} source={source}/>
            </div>
        );
    }

    const _renderSupportTicketCreateHeader = () => {
        return (
            <div className="header header-arrow-space-between">
                <div
                    className="header-arrow-box"
                    onClick={() => {
                        if (settings?.projects?.authType === 'postMessage') {
                            setModalContent({ component: <SupportTickets/>, className: 'support-tickets-modal' });
                            return;
                        }

                        navigate('/support/tickets')}
                    }
                >
                    <ArrowBackSvg fill="#ffff"/>
                    {t('common.back')}
                </div>
                <Closure origin={origin} source={source}/>
            </div>
        );
    }

    if (origin === 'auth') {
        return _renderAuthHeader();
    } else if (origin === 'payments') {
        return _renderPaymentsHeader();
    } else if (['transactions', 'transactionHelper'].includes(origin)) {
        return _renderTransactionsHeader();
    } else if (origin === 'ticketList') {
        return _renderSupportTicketListHeader();
    } else if (origin === 'firstTicketCreate') {
        return _renderSupportFirstTicketCreateHeader();
    } else if (origin === 'ticketCreate' || origin === 'ticketItem') {
        return _renderSupportTicketCreateHeader();
    }

    console.error('HEADER_ORIGIN_IS_NOT_VALID', { source, origin });
    return null;
}